import { Maybe, MaybeUndef } from 'types/UtilityTypes';

/**
 * Calculates density in lb/cu ft for a commodity
 * Formula: density = weight_lb / (pallet_count × volume_per_pallet_cuft)
 *
 * where:
 * - weight_lb = wt_per_unit × 2.20462262 (if kg) or wt_per_unit (if lb)
 * - volume_per_pallet = (length/12) × (width/12) × (height/12) if inches, or L × W × H if feet
 *
 * @param weightAmount - Weight per piece
 * @param weightUnitId - 0 for kg, 1 for lbs
 * @param length - Length dimension
 * @param width - Width dimension
 * @param height - Height dimension
 * @param dimUnitId - 0 for inches, 1 for feet
 * @param handlingUnitCount - Number of pallets
 * @returns Density in lb/cu ft, or null if calculation cannot be performed
 */
export function calculateDensity(
  weightAmount: MaybeUndef<number>,
  weightUnitId: MaybeUndef<number>,
  length: MaybeUndef<number>,
  width: MaybeUndef<number>,
  height: MaybeUndef<number>,
  dimUnitId: MaybeUndef<number>,
  handlingUnitCount: MaybeUndef<number>
): Maybe<number> {
  // Validate required inputs
  if (
    !length ||
    !width ||
    !height ||
    !weightAmount ||
    weightAmount <= 0 ||
    !handlingUnitCount ||
    handlingUnitCount <= 0
  ) {
    return null;
  }

  // Convert weight to pounds (lb)
  const weightLb =
    weightUnitId === 0
      ? weightAmount * 2.20462262 // Kg to lbs: wt_per_unit × 2.20462262
      : weightAmount; // If unit is lb, weightLb = wt_per_unit

  // Convert dimensions to feet (ft)
  const lengthFt = dimUnitId === 0 ? length / 12 : length; // Inches to feet: divide by 12
  const widthFt = dimUnitId === 0 ? width / 12 : width;
  const heightFt = dimUnitId === 0 ? height / 12 : height;
  // If unit is feet, dimensions are already in feet

  // Volume per pallet (cubic feet)
  const volumePerPalletCuFt = lengthFt * widthFt * heightFt;
  if (volumePerPalletCuFt <= 0) {
    return null;
  }

  // Total volume = pallet_count × volume_per_pallet
  const totalVolumeCuFt = handlingUnitCount * volumePerPalletCuFt;
  if (totalVolumeCuFt <= 0) {
    return null;
  }

  // Density (lb / cu ft) = weight_lb / total_volume_cuft
  // Return full precision value (will be rounded for display only)
  return weightLb / totalVolumeCuFt;
}
